import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { identifyUser, resetUser, capture } from '@/lib/analytics/posthog';

async function recordAudit(action: string, metadata?: Record<string, unknown>) {
  try {
    await supabase.functions.invoke('record-audit', { body: { action, metadata } });
  } catch {
    // fire-and-forget, never throw
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_IN' && session?.user) {
          identifyUser(session.user.id, { email: session.user.email });
          capture('signin');
          void recordAudit('signin');
        } else if (event === 'SIGNED_OUT') {
          capture('signout');
          void recordAudit('signout');
          resetUser();
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + '/auth',
        data: { name },
      },
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    // Clear all pending checkout state
    localStorage.removeItem('pending_checkout_plan');
    localStorage.removeItem('pending_checkout_billing');
    localStorage.removeItem('pending_plan_selection');
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };
}
